"use client"

import { useState, useEffect, useRef, useMemo } from "react"
import "../../App.css"
import norecords from "../../assests/norecords.gif"
import { useNavigate, useLocation } from "react-router-dom"
import CustomTooltip from "../Buttons/CustomTooltip"
import CustomButton from "../Buttons/CustomButton"
import DownloadWithPopup from "../migrations/DownloadPopup"
import axios from "axios"
import { showToast } from "../Buttons/Toaster"
import DeleteConfirmationModal from "../migrations/DeleteConfirmationModal"

const cellStyle = {
  textAlign: "left",
  verticalAlign: "middle",
}

// Defined at module scope so its reference is stable across renders -
// otherwise every re-render of SourceProfileTable (e.g. the ~5s websocket
// updates, or any checkbox click) would create a new component type for
// this and force React to unmount/remount every instance instead of just
// patching them in place, which gets slow fast on a table that updates
// frequently.
const TruncatedText = ({
  text,
  maxLength = 18,
  jobId,
  forceTooltipJobId,
  tooltipVisible,
}) => {
  if (!text) return "N/A"
  const isTruncated = text.length > maxLength
  const truncated = isTruncated ? `${text.substring(0, maxLength)}...` : text
  return isTruncated || forceTooltipJobId === jobId ? (
    <CustomTooltip
      content={text}
      placement="right"
      fallbackPlacements={["top"]}
      forceShow={forceTooltipJobId === jobId && tooltipVisible}
    >
      <span style={{ cursor: "pointer" }}>{truncated}</span>
    </CustomTooltip>
  ) : (
    <span>{truncated}</span>
  )
}

function SourceProfileTable({
  onCreateSourceClick,
  refreshKey,
  onEditJob,
  setShowEdit,
  forceTooltipJobId,
  tooltipVisible,
  setRefreshKey,
  onViewLogs,
  onViewVisualSummary,
  onViewProgress,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [checkedRows, setCheckedRows] = useState({})
  const [searchQuery, setSearchQuery] = useState("")
  const [activeJobs, setActiveJobs] = useState([])
  const [draftJobs, setDraftJobs] = useState([])
  const [deletedJobs, setDeletedJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const headerCheckboxRef = useRef(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedJobsForDelete, setSelectedJobsForDelete] = useState([])
  const [activeTab, setActiveTab] = useState("activejobs")
  const [isDeletePermanentlyEnabled, setIsDeletePermanentlyEnabled] = useState(false)
  const [isCancelEnabled, setIsCancelEnabled] = useState(false)
  const [isRestoring, setIsRestoring] = useState(false)
  const [isRestoreEnabled, setIsRestoreEnabled] = useState(false)
  const [modalAction, setModalAction] = useState("delete")

  const isDeletedSourcesPage = location.pathname.includes("/deleted_sources")
  const socketRef = useRef(null)
  const retryDelayRef = useRef(1000)
  const maxRetryDelay = 30000
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 5
  const intentionalReconnectRef = useRef(false) // Add this flag

  useEffect(() => {
    if (location.pathname.includes("/draft_jobs")) {
      setActiveTab("draftjobs")
    } else {
      setActiveTab("activejobs")
    }
  }, [location.pathname])

  const data = useMemo(() => {
    if (isDeletedSourcesPage) return deletedJobs
    return activeTab === "draftjobs" ? draftJobs : activeJobs
  }, [activeTab, activeJobs, draftJobs, deletedJobs, isDeletedSourcesPage])

  const generateRowKey = (job) => String(job.id)

  useEffect(() => {
    let isComponentMounted = true
    let reconnectTimeoutId = null
    let connectionAttemptInProgress = false

    const connectWebSocket = () => {
      // Prevent multiple simultaneous connection attempts
      if (connectionAttemptInProgress || !isComponentMounted) {
        return
      }

      connectionAttemptInProgress = true

      const authToken = sessionStorage.getItem("access_token")
      if (!authToken) {
        setError("Authentication token missing")
        setLoading(false)
        connectionAttemptInProgress = false
        return
      }

      // Clear any existing socket before creating new one
      if (socketRef.current && socketRef.current.readyState !== WebSocket.CLOSED) {
        socketRef.current.onclose = null
        socketRef.current.onerror = null
        socketRef.current.onmessage = null
        socketRef.current.close()
        socketRef.current = null
      }

      // Set flag for intentional reconnect only if this is a retry
      if (reconnectAttemptsRef.current > 0) {
        intentionalReconnectRef.current = true
      }

      const wsBaseList = process.env.REACT_APP_API_URL_SOURCE_PROFILER

      try {
        socketRef.current = new WebSocket(`${wsBaseList}/ws/profiler_jobs_list?token=${authToken}`)
      } catch (error) {
        console.error("Failed to create WebSocket:", error)
        connectionAttemptInProgress = false
        if (isComponentMounted && !intentionalReconnectRef.current) {
          setError("Failed to establish connection")
        }
        return
      }

      socketRef.current.onopen = () => {
        if (!isComponentMounted) {
          socketRef.current?.close()
          return
        }

        console.log("WebSocket connection established")
        setError(null)
        setLoading(false)
        retryDelayRef.current = 1000
        reconnectAttemptsRef.current = 0
        intentionalReconnectRef.current = false
        connectionAttemptInProgress = false
      }

      socketRef.current.onmessage = (event) => {
        if (!isComponentMounted) return

        try {
          const message = JSON.parse(event.data)
          console.log("WebSocket message received:", message)

          if (["initial_load", "update"].includes(message.event)) {
            const processedActiveJobs = (message.data.profiler_jobs || [])
              .filter((job) => job.status !== "Draft")
              .map((job) => ({
                id: job.job_id,
                job: job.job_name || "N/A",
                description: job.description || "N/A",
                source_db: job.source_db || "N/A",
                complexity: job.complexity || 0,
                completed_at: job.completed_at || "N/A",
                started_at: job.started_at || "N/A",
                status: job.status || "N/A",
              }))

            const processedDraftJobs = (message.data.draft_jobs || []).map((job) => ({
              id: job.job_id,
              job: job.job_name || "N/A",
              description: job.description || "N/A",
              source_db: job.source_db || "N/A",
              complexity: job.complexity || 0,
              completed_at: job.completed_at || "N/A",
              started_at: job.started_at || "N/A",
              status: job.status || "N/A",
            }))

            const processedDeletedJobs = (message.data.inactive_jobs || []).map((job) => ({
              id: job.job_id,
              job: job.job_name || "N/A",
              description: job.description || "N/A",
              source_db: job.source_db || "N/A",
              complexity: job.complexity || 0,
              completed_at: job.completed_at || "N/A",
              started_at: job.started_at || "N/A",
              status: job.status || "Inactive",
            }))

            setActiveJobs(Array.from(new Map(processedActiveJobs.map((job) => [job.id, job])).values()))
            setDraftJobs(Array.from(new Map(processedDraftJobs.map((job) => [job.id, job])).values()))
            setDeletedJobs(Array.from(new Map(processedDeletedJobs.map((job) => [job.id, job])).values()))
            console.log("Updated jobs state from WebSocket")
          }
        } catch (err) {
          console.error("WebSocket message parsing error:", err)
          // Don't show error for parsing issues during intentional reconnects
          if (!intentionalReconnectRef.current && isComponentMounted) {
            setError("Failed to parse WebSocket message")
          }
        }
      }

      socketRef.current.onclose = (event) => {
        connectionAttemptInProgress = false

        if (!isComponentMounted || intentionalReconnectRef.current) {
          console.log("WebSocket closed intentionally")
          return
        }

        if (!event.wasClean) {
          console.warn("WebSocket closed unexpectedly:", event)
        }

        // Only attempt reconnect if component is still mounted and page is visible
        if (isComponentMounted && !document.hidden) {
          reconnectAttemptsRef.current += 1

          if (reconnectAttemptsRef.current <= maxReconnectAttempts) {
            const delay = Math.min(retryDelayRef.current * 1.5, maxRetryDelay)
            console.log(`Attempting reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current})`)

            // Only show error message on first reconnect attempt
            if (reconnectAttemptsRef.current === 1) {
              setError("Connection lost. Reconnecting...")
            }

            reconnectTimeoutId = setTimeout(() => {
              if (isComponentMounted && !intentionalReconnectRef.current) {
                connectWebSocket()
              }
            }, delay)

            retryDelayRef.current = delay
          } else {
            setError("Unable to establish connection. Please refresh the page.")
            console.error("Max reconnection attempts reached")
          }
        }
      }

      socketRef.current.onerror = (err) => {
        console.error("WebSocket error:", err)
        connectionAttemptInProgress = false

        // Only show error if it's not an intentional reconnect and component is mounted
        if (!intentionalReconnectRef.current && isComponentMounted) {
          // Don't show error immediately, let the onclose handler manage reconnection
          console.log("WebSocket error occurred, waiting for close event")
        }

        // Don't close the socket here, let it close naturally and trigger onclose
      }
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log("Page hidden, marking for intentional close")
        intentionalReconnectRef.current = true

        // Clear any pending reconnection attempts
        if (reconnectTimeoutId) {
          clearTimeout(reconnectTimeoutId)
          reconnectTimeoutId = null
        }

        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.close()
        }
      } else {
        console.log("Page visible, preparing for reconnection")
        intentionalReconnectRef.current = false
        reconnectAttemptsRef.current = 0

        // Add a small delay to ensure clean reconnection
        if (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED) {
          setTimeout(() => {
            if (isComponentMounted && !intentionalReconnectRef.current) {
              connectWebSocket()
            }
          }, 200)
        }
      }
    }

    // Reset all state on refresh
    reconnectAttemptsRef.current = 0
    intentionalReconnectRef.current = false
    retryDelayRef.current = 1000

    console.log("Initializing WebSocket connection")

    // Add a small initial delay to ensure component is fully mounted
    setTimeout(() => {
      if (isComponentMounted) {
        connectWebSocket()
      }
    }, 100)

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      console.log("Cleaning up WebSocket")
      isComponentMounted = false
      intentionalReconnectRef.current = true

      // Clear any pending reconnection attempts
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId)
      }

      // Clean up WebSocket
      if (socketRef.current) {
        socketRef.current.onclose = null
        socketRef.current.onerror = null
        socketRef.current.onmessage = null
        socketRef.current.close()
        socketRef.current = null
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [refreshKey])

  // Rest of your component code remains the same...
  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase()
    return data.filter(
      (row) =>
        row.job.toLowerCase().includes(query) ||
        (row.status && row.status.toLowerCase().includes(query)) ||
        (row.source_db && row.source_db.toLowerCase().includes(query)),
    )
  }, [data, searchQuery])

  const allRowKeys = useMemo(() => filteredData.map(generateRowKey), [filteredData])

  const { allSelected, someSelected } = useMemo(() => {
    const selectedCount = allRowKeys.filter((key) => checkedRows[key]).length
    return {
      allSelected: filteredData.length > 0 && selectedCount === filteredData.length,
      someSelected: selectedCount > 0 && selectedCount < filteredData.length,
    }
  }, [checkedRows, allRowKeys, filteredData])

const handleCloseModal = () => {
  setShowDeleteModal(false);
  resetModalState();
};
const resetModalState = () => {
  setCheckedRows({});
  setSelectedJobsForDelete([]);
  setModalAction("delete");
};


  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected
      headerCheckboxRef.current.checked = allSelected
    }
  }, [someSelected, allSelected])

  const handleSelectAllChange = () => {
    if (allSelected) {
      setCheckedRows({})
    } else {
      const newChecked = {}
      allRowKeys.forEach((key) => {
        newChecked[key] = true
      })
      setCheckedRows(newChecked)
    }
  }

  const handleCheckboxChange = (rowKey) => {
    setCheckedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }))
  }

  const hasSelectedJob = () => Object.values(checkedRows).filter(Boolean).length === 1

  const getSelectedJob = () => {
    const selectedKey = Object.keys(checkedRows).find((key) => checkedRows[key])
    if (!selectedKey) return null
    const job = data.find((j) => String(j.id) === selectedKey)
    return job
      ? {
          ...job,
          job_id: job.id,
          job_name: job.job,
          job_description: job.description,
        }
      : null
  }

  const handleEditClick = () => {
    if (hasSelectedJob()) {
      const selectedJob = getSelectedJob()
      if (selectedJob) {
        onEditJob(selectedJob)
        setShowEdit(true)
      }
    }
  }

    const showSourceProfilerToast = (message, type = "success", options = {}) => {
    const defaultOptions = {
      toastId: `source-profiler-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
    return showToast(message, type, { ...defaultOptions, ...options });
  };


  const handleReScan = async (jobId) => {
    const wsBaseScan = process.env.REACT_APP_API_URL_SOURCE_PROFILER
    const isDraftTab = activeTab === "draftjobs"
    const toastMsg = isDraftTab
      ? "The draft job execution started, this job is now moving to the active scan job."
      : "Scan started"
    
    // FIX: Use scoped toast
    const toastId = showSourceProfilerToast(toastMsg, "success", { autoClose: 3000 })

    try {
      const authToken = sessionStorage.getItem("access_token")
      if (!authToken) throw new Error("Authentication token missing.")

      const response = await axios.get(`${wsBaseScan}/scan?job_ids=${jobId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      })

      setTimeout(() => {
        if (response.status === 200) {
          const finalToastMsg = isDraftTab ? "Draft job scan completed successfully" : "Scan completed successfully"
          // FIX: Use scoped toast
          showSourceProfilerToast(finalToastMsg, "success")
          setRefreshKey((prev) => prev + 1)
        } else {
          throw new Error(response.data?.message || "Failed to initiate scan")
        }
      }, 3000)
    } catch (error) {
      setTimeout(() => {
        console.error("Scan failed:", error)
        // FIX: Use scoped toast
        showSourceProfilerToast(error.response?.data?.detail || error.message || "Scan failed", "error")
      }, 3000)
    }
  }


  useEffect(() => {
    const selectedCount = Object.values(checkedRows).filter(Boolean).length
    const isEnabled = selectedCount > 0
    setIsDeletePermanentlyEnabled(isEnabled)
    setIsRestoreEnabled(isEnabled)
    setIsCancelEnabled(isEnabled)
  }, [checkedRows])

  useEffect(() => {
    const selectedCount = Object.values(checkedRows).filter(Boolean).length
    setIsRestoreEnabled(selectedCount > 0)
  }, [checkedRows])

 const handleRestoreJob = async (jobId) => {
    setIsRestoring(true)
    try {
      const authToken = sessionStorage.getItem("access_token")
      if (!authToken) throw new Error("Authentication token missing")

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL_SOURCE_PROFILER}/restore_profiler_jobs/${jobId}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        },
      )

      if (response.status === 200) {
        // FIX: Use scoped toast
        showSourceProfilerToast("Job restored successfully", "success")
        setRefreshKey((prev) => prev + 1)
        setCheckedRows({})
      } else {
        throw new Error(response.data?.message || "Failed to restore job")
      }
    } catch (error) {
      console.error("Restore failed:", error)
      // FIX: Use scoped toast
      showSourceProfilerToast(error.response?.data?.detail || error.message || "Restore failed", "error")
    } finally {
      setIsRestoring(false)
    }
  }


 const handleBulkRestore = async () => {
    setIsRestoring(true)
    try {
      const authToken = sessionStorage.getItem("access_token")
      if (!authToken) throw new Error("Authentication token missing")

      const jobIds = Object.keys(checkedRows)
        .filter((key) => checkedRows[key])
        .map((key) => {
          const job = data.find((job) => generateRowKey(job) === key)
          return job?.id
        })
        .filter((id) => id !== undefined)

      if (jobIds.length === 0) {
        throw new Error("No valid jobs selected for restore")
      }

      const restorePromises = jobIds.map((jobId) =>
        axios.put(`${process.env.REACT_APP_API_URL_SOURCE_PROFILER}/restore_profiler_jobs/${jobId}`, null, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }),
      )

      const results = await Promise.all(restorePromises)
      const allSuccess = results.every((response) => response.status === 200)

      if (allSuccess) {
        // FIX: Use scoped toast
        showSourceProfilerToast(jobIds.length === 1 ? "Job restored successfully" : "Selected jobs restored successfully", "success")
        setRefreshKey((prev) => prev + 1)
        setCheckedRows({})
        setShowDeleteModal(false)
      } else {
        throw new Error("Some jobs failed to restore")
      }
    } catch (error) {
      console.error("Bulk restore failed:", error)
      // FIX: Use scoped toast
      showSourceProfilerToast(error.response?.data?.detail || error.message || "Bulk restore failed", "error")
    } finally {
      setIsRestoring(false)
    }
  }

  const handleCancelClick = () => {
    setCheckedRows({})
  }

 const handlePermanentDelete = async () => {
    setIsDeleting(true)
    try {
      const authToken = sessionStorage.getItem("access_token")
      if (!authToken) throw new Error("Authentication token missing")

      const jobIds = selectedJobsForDelete.map((job) => job.id)
      const apiUrl = `${
        process.env.REACT_APP_API_URL_SOURCE_PROFILER
      }/permanently_delete_profiler_jobs?job_ids=${jobIds.join(",")}`

      await axios.delete(apiUrl, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      })

      // FIX: Use scoped toast
      showSourceProfilerToast(
        selectedJobsForDelete.length === 1
          ? "Job permanently deleted successfully"
          : "Selected jobs permanently deleted successfully",
        "success",
      )
      setRefreshKey((prev) => prev + 1)
      setCheckedRows({})
      setSelectedJobsForDelete([])
    } catch (error) {
      console.error("Permanent delete failed:", error)
      // FIX: Use scoped toast
      showSourceProfilerToast(
        error.response?.data?.message ||
          (selectedJobsForDelete.length === 1
            ? "Failed to permanently delete job"
            : "Failed to permanently delete jobs"),
        "error",
      )
    } finally {
      setIsDeleting(false)
      setShowDeleteModal(false)
    }
  }

  const renderActions = (row) => {
    const rowKey = generateRowKey(row)
    const isSelected = checkedRows[rowKey] || false

    if (isDeletedSourcesPage) {
      return (
      <div className="dropdown d-sm-inline-block position-relative">
      <i className="fas fa-ellipsis-v cursor-pointer" data-bs-toggle="dropdown"></i>
      <div className="dropdown-menu dropdown-menu-end min-width-120px">
        <button
          className="dropdown-item notify-item"
          onClick={handleRestoreClick}
          disabled={!isSelected || isRestoring}
        >
          <p className="font-size-12 mb-0">
            <i className="fa-solid fa-rotate-left pe-2"></i>
            {isRestoring ? "Restoring..." : "Restore"}
          </p>
        </button>
        <button
          className="dropdown-item notify-item"
          onClick={handlePermanentDeleteClick}
          disabled={!isSelected || isDeleting}
        >
          <p className="font-size-12 mb-0">
            <i className="fa-solid fa-trash pe-2"></i>
            {isDeleting ? "Deleting..." : "Delete Permanently"}
          </p>
        </button>
      </div>
    </div>
      )
    }

    if (activeTab === "draftjobs") {
      return (
        <div className="dropdown d-sm-inline-block position-relative">
          <i className="fas fa-ellipsis-v cursor-pointer" data-bs-toggle="dropdown"></i>
          <div className="dropdown-menu dropdown-menu-end min-width-120px">
            <div style={!isSelected ? { opacity: 0.5, pointerEvents: "none" } : {}}>
              <button className="dropdown-item notify-item" onClick={() => handleReScan(row.id)} disabled={!isSelected}>
                <p className="font-size-12 mb-0">
                  <i className="fa-solid fa-rotate-right pe-2"></i>Re-Scan
                </p>
              </button>
            </div>
          </div>
        </div>
      )
    }

    const isFailedStatus = row.status === "Failed"
    return (
      <div className="dropdown d-sm-inline-block position-relative">
        <i className="fas fa-ellipsis-v cursor-pointer" data-bs-toggle="dropdown"></i>
        <div className="dropdown-menu dropdown-menu-end min-width-120px">
          <div style={!isSelected ? { opacity: 0.5, pointerEvents: "none" } : {}}>
            {activeTab === "activejobs" && isFailedStatus && (
              <button className="dropdown-item notify-item" onClick={() => handleReScan(row.id)} disabled={!isSelected}>
                <p className="font-size-12 mb-0">
                  <i className="fa-solid fa-rotate-right pe-2"></i>Re-Scan
                </p>
              </button>
            )}
            {(isFailedStatus || row.status === "In Progress") && (
              <button
                className="dropdown-item notify-item"
                onClick={() => onViewProgress(row.id, row.job)}
                disabled={!isSelected}
              >
                <p className="font-size-12 mb-0">
                  <i className="fa-solid fa-chart-line pe-2"></i>View Progress
                </p>
              </button>
            )}
            <DownloadWithPopup
              jobId={row.id}
              jobName={row.job}
              onConfirm={handleDownload}
              className={`dropdown-item notify-item ${!isSelected ? "disabled" : ""}`}
              title="Download Script"
              headingText="Your script is ready!"
              confirmText="Proceed with download?"
              customDetailsJSX={
                <p className="p_styles">
                  <strong>Details : Job Name:</strong> {row.job}
                </p>
              }
            >
              <p className="font-size-12 mb-0">
                <i className="fa-solid fa-download pe-2"></i>Download
              </p>
            </DownloadWithPopup>
            {activeTab === "activejobs" && (
              <button
                className="dropdown-item notify-item"
                onClick={() => onViewLogs(row.id, row.job)}
                disabled={!isSelected}
              >
                <p className="font-size-12 mb-0">
                  <i className="fa-solid fa-file-lines pe-2"></i>View Logs
                </p>
              </button>
            )}
            {activeTab === "activejobs" && !isFailedStatus && (
              <button
                className="dropdown-item notify-item"
                onClick={() => onViewVisualSummary(row.id)}
                disabled={!isSelected}
              >
                <p className="font-size-12 mb-0">
                  <i className="fa-solid fa-chart-pie pe-2"></i>View Visual Summary
                </p>
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  const handleDownload = async (jobId) => {
    const wsBaseDownloadSource = process.env.REACT_APP_API_URL_SOURCE_PROFILER
    try {
      const authToken = sessionStorage.getItem("access_token")
      if (!authToken) throw new Error("Authentication token missing.")

      const response = await axios.get(`${wsBaseDownloadSource}/download_report/${jobId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
        responseType: "blob",
      })

      const contentDisposition = response.headers["content-disposition"]
      const filename = contentDisposition?.split("filename=")[1]?.replace(/"/g, "") || `profiler-report-${jobId}.zip`

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      setTimeout(() => {
        // FIX: Use scoped toast
        showSourceProfilerToast("Script downloaded successfully.", "success")
      }, 2000)
    } catch (error) {
      console.error("Download failed:", error)
      // FIX: Use scoped toast
      showSourceProfilerToast("Failed to download script", "error")
    }
  }

 const handleDeleteClick = () => {
  const selectedJobs = data.filter((job) => checkedRows[generateRowKey(job)]);
  setSelectedJobsForDelete(selectedJobs);
  setModalAction("delete");
  setShowDeleteModal(true);
};

const handlePermanentDeleteClick = () => {
  const selectedJobs = data.filter((job) => checkedRows[generateRowKey(job)]);
  setSelectedJobsForDelete(selectedJobs);
  setModalAction("delete permanently");
  setShowDeleteModal(true);
};

const handleRestoreClick = () => {
  const selectedJobs = data.filter((job) => checkedRows[generateRowKey(job)]);
  setSelectedJobsForDelete(selectedJobs);
  setModalAction("restore");
  setShowDeleteModal(true);
};
 const handleConfirmDelete = async () => {
    setIsDeleting(true)
    try {
      const authToken = sessionStorage.getItem("access_token")
      if (!authToken) throw new Error("Authentication token missing")

      const jobIds = selectedJobsForDelete.map((job) => job.id)
      const queryString = jobIds.map((id) => `job_ids=${id}`).join("&")

      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL_SOURCE_PROFILER}/delete_profiler_jobs/?${queryString}`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      )

      if (response.status === 200) {
        // FIX: Use scoped toast
        showSourceProfilerToast(
          selectedJobsForDelete.length === 1 ? "Job deleted successfully" : "Selected jobs deleted successfully",
          "success",
          {
            toastId: `source-profiler-delete-${Date.now()}`
          }
        )
        setRefreshKey((prev) => prev + 1)
        setCheckedRows({})
        setSelectedJobsForDelete([])
      } else {
        throw new Error(response.data?.message || "Failed to delete")
      }
    } catch (error) {
      console.error("Delete failed:", error)
      // FIX: Use scoped toast
      showSourceProfilerToast(
        selectedJobsForDelete.length === 1 ? "Failed to delete job" : "Failed to delete jobs", 
        "error",
        {
          toastId: `source-profiler-delete-error-${Date.now()}`
        }
      )
    } finally {
      setIsDeleting(false)
      setShowDeleteModal(false)
    }
  }


  const isDeleteEnabled = Object.values(checkedRows).some(Boolean) && !isDeletedSourcesPage

  if (loading) {
    return (
      <div className="migration-table-container">
        <div className="d-flex justify-content-between mb-3">
          <div className="loadingStyle" style={{ width: "300px", height: "40px" }}></div>
          <div className="loadingStyle" style={{ width: "200px", height: "40px" }}></div>
        </div>
        <table className="table">
          <thead>
            <tr>
              {[...Array(6)].map((_, i) => (
                <th key={i}>
                  <div className="loadingStyle" style={{ width: "80%", height: "20px" }}></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, rowIndex) => (
              <tr key={rowIndex}>
                {[...Array(6)].map((_, cellIndex) => (
                  <td key={cellIndex}>
                    <div
                      className="loadingStyle"
                      style={{
                        width: cellIndex === 0 ? "20px" : `${80 + Math.random() * 20}%`,
                        height: "16px",
                        margin: cellIndex === 0 ? "0 auto" : "0",
                      }}
                    ></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if (error) return <div className="text-center mt-3 text-danger">Error: {error}</div>

  return (
    <div className="migration-table-container">
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="fw-bold mb-0">Source Profiler</h5>
          <div className="page-title-right">
            <ol className="breadcrumb m-0 p-0">
              <li className="breadcrumb-item font-size-12">
                <a
                  href="/home"
                  onClick={(e) => {
                    e.preventDefault()
                    navigate("/home")
                  }}
                >
                  Home
                </a>
              </li>
              <li className="breadcrumb-item font-size-12 active ps-0 pe-0">
                <span className="text-dark">
                  {isDeletedSourcesPage
                    ? "Deleted Sources"
                    : activeTab === "draftjobs"
                      ? "Draft Jobs"
                      : "Profiler List"}
                </span>
              </li>
            </ol>
          </div>
        </div>
        <div className="d-flex gap-2 ms-auto">
          {!isDeletedSourcesPage && (
            <CustomButton onClick={handleEditClick} text="Edit" isEditEnabled={hasSelectedJob()} />
          )}
          {!isDeletedSourcesPage && (
            <CustomButton
              onClick={handleDeleteClick}
              text="Delete"
              isDeleteEnabled={isDeleteEnabled}
              isDisabled={!isDeleteEnabled}
            />
          )}
          {!isDeletedSourcesPage && activeTab === "activejobs" && (
            <CustomButton
              text="Create New Scan Job"
              className="btn btn-primary custom-button"
              onClick={onCreateSourceClick}
            />
          )}
         {isDeletedSourcesPage && (
  <>
    <CustomButton
      text="Cancel"
      className="btn btn-edit custom-button"
      onClick={resetModalState}
      isEnabled={isCancelEnabled}
      isDisabled={!isCancelEnabled}
    />
    <CustomButton
      text="Delete Permanently"
      onClick={handlePermanentDeleteClick}
      isEnabled={isDeletePermanentlyEnabled}
      isDisabled={!isDeletePermanentlyEnabled}
      className="btn-danger"
    />
    <CustomButton
      text="Restore"
      onClick={handleRestoreClick}
      isEnabled={isRestoreEnabled}
      isDisabled={!isRestoreEnabled}
      className="btn-primary"
    />
  </>
)}
        </div>
      </div>

      <div className="search-bar my-2 position-relative">
        <span
          className="ri-search-line position-absolute"
          style={{
            top: "50%",
            left: "10px",
            transform: "translateY(-50%)",
            fontSize: "16px",
          }}
        ></span>
        <input
          type="text"
          placeholder="Search"
          className="form-control highlighted-input styled-input font-size-12 doc-search"
          style={{ paddingLeft: "30px" }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {!isDeletedSourcesPage && (
        <ul className="nav nav-tabs nav-tabs-custom nav-justified w-fit-content mb-1" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link tab-col w-fit-content ${activeTab === "activejobs" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("activejobs")
                navigate("/sourceprofiler")
              }}
              style={{
                backgroundColor: activeTab === "activejobs" ? "#dcdfe3" : "transparent",
                borderRadius: "8px 8px 0 0",
                padding: "10px 20px",
                width: "140px",
                height: "29px",
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "21px",
                letterSpacing: "0%",
                position: "relative",
                zIndex: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                border: "none",
                cursor: "pointer",
              }}
            >
              <span className="d-sm-block white-space-nowrap">
                Active Scan Jobs
                {activeTab === "activejobs" && (
                  <span
                    style={{
                      display: "block",
                      height: "1px",
                      backgroundColor: "#354258",
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      zIndex: 4,
                    }}
                  />
                )}
              </span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link tab-col w-fit-content ${activeTab === "draftjobs" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("draftjobs")
                navigate("/sourceprofiler/draft_jobs")
              }}
              style={{
                backgroundColor: activeTab === "draftjobs" ? "#dcdfe3" : "transparent",
                borderRadius: "8px 8px 0 0",
                padding: "10px 20px",
                width: "140px",
                height: "29px",
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "21px",
                letterSpacing: "0%",
                position: "relative",
                zIndex: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                border: "none",
                cursor: "pointer",
              }}
            >
              <span className="d-sm-block white-space-nowrap">
                Draft Jobs
                {activeTab === "draftjobs" && (
                  <span
                    style={{
                      display: "block",
                      height: "1px",
                      backgroundColor: "#354258",
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      zIndex: 4,
                    }}
                  />
                )}
              </span>
            </button>
          </li>
        </ul>
      )}

      {isDeletedSourcesPage && (
        <ul className="nav nav-tabs nav-tabs-custom nav-justified w-fit-content mb-1" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className="nav-link w-fit-content active"
              style={{
                backgroundColor: "#dcdfe3",
                borderRadius: "8px 8px 0 0",
                padding: "10px 20px",
                width: "140px",
                height: "29px",
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "21px",
                letterSpacing: "0%",
                color: "#354258",
                position: "relative",
                zIndex: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                border: "none",
                cursor: "default",
              }}
            >
              <span className="d-sm-block white-space-nowrap">
                Deleted Jobs
                <span
                  style={{
                    display: "block",
                    height: "1px",
                    backgroundColor: "#354258",
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                />
              </span>
            </button>
          </li>
        </ul>
      )}

      <div className="tab-content tab-con">
        <div
          className="tab-pane active"
          id="sourceprofiler"
          role="tabpanel"
          style={{
            maxHeight: "67vh",
            overflowY: "scroll",
            position: "relative",
            overflowX: "visible",
            zIndex: 1,
            minHeight: "67vh",
          }}
        >
          <table className="table">
            <thead className="table-head fw-bold">
              <tr>
                <th style={{ ...cellStyle, fontSize: "16px" }}>
                  <input
                    type="checkbox"
                    ref={headerCheckboxRef}
                    onChange={handleSelectAllChange}
                    className="custom-checkbox"
                  />
                </th>
                <th style={{ ...cellStyle, width: "16%", fontSize: "11px" }}>Job Name</th>
                <th style={{ ...cellStyle, width: "17%", fontSize: "11px" }}>Description</th>
                <th style={{ ...cellStyle, width: "18%", fontSize: "11px" }}>Source Type</th>
                <th style={{ ...cellStyle, width: "12%", fontSize: "11px" }}>Status</th>
                <th style={{ ...cellStyle, width: "15%", fontSize: "11px" }}>Started</th>
                {(activeTab === "activejobs" || isDeletedSourcesPage) && (
                  <th style={{ ...cellStyle, width: "15%", fontSize: "11px" }}>Completed</th>
                )}
                <th style={{ ...cellStyle, width: "10%", fontSize: "11px" }}>Actions</th>
              </tr>
            </thead>
            <tbody style={{ zIndex: 1 }}>
              {filteredData.length > 0 ? (
                filteredData.map((row) => {
                  const rowKey = generateRowKey(row)
                  const isSelected = checkedRows[rowKey] || false
                  const statusClass = row.status ? row.status.toLowerCase().replace(/\s+/g, "-") : "default"

                  return (
                    <tr
                      key={rowKey}
                      style={{
                        backgroundColor: isSelected ? "#F1F3FB" : "transparent",
                      }}
                    >
                      <td style={cellStyle}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleCheckboxChange(rowKey)}
                          className="custom-checkbox"
                        />
                      </td>
                      <td style={{ ...cellStyle, width: "20%" }}>
                        <TruncatedText
                          text={row.job}
                          maxLength={20}
                          jobId={row.id}
                          forceTooltipJobId={forceTooltipJobId}
                          tooltipVisible={tooltipVisible}
                        />
                      </td>
                      <td style={{ ...cellStyle, width: "28%" }}>
                        <TruncatedText
                          text={row.description}
                          maxLength={39}
                          forceTooltipJobId={forceTooltipJobId}
                          tooltipVisible={tooltipVisible}
                        />
                      </td>
                      <td style={{ ...cellStyle, width: "15%" }}>
                        <TruncatedText
                          text={row.source_db}
                          maxLength={17}
                          forceTooltipJobId={forceTooltipJobId}
                          tooltipVisible={tooltipVisible}
                        />
                      </td>
                      <td style={{ ...cellStyle, width: "15%" }}>
                        <div className={`status-container ${statusClass}`}>{row.status || "N/A"}</div>
                      </td>
                      <td
                        style={{
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <i className="ri-calendar-2-line" style={{ fontSize: "14px", marginRight: "4px" }} />
                          <span>{row.started_at || "N/A"}</span>
                        </div>
                      </td>
                      {(activeTab === "activejobs" || isDeletedSourcesPage) && (
                        <td
                          style={{
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <i className="ri-calendar-2-line" style={{ fontSize: "14px", marginRight: "4px" }} />
                            <span>{row.completed_at || "N/A"}</span>
                          </div>
                        </td>
                      )}
                      <td style={{ textAlign: "center" }}>{renderActions(row)}</td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={isDeletedSourcesPage ? 8 : activeTab === "activejobs" ? 8 : 7}>
                    <div className="text-center" style={{ height: "50vh", paddingTop: "30px" }}>
                      <img src={norecords || "/placeholder.svg"} alt="No Records" style={{ width: "220px" }} />
                      <h4 className="fw-bold pt-3" style={{ fontSize: "18px" }}>
                        {isDeletedSourcesPage
                          ? "No deleted sources found"
                          : activeTab === "activejobs"
                            ? "No active scan jobs found"
                            : "No draft jobs found"}
                      </h4>
                      <p className="text-muted">
                        {isDeletedSourcesPage
                          ? "When you delete sources, they will appear here."
                          : activeTab === "activejobs"
                            ? "Create a new scan job to get started."
                            : "Create or edit a draft job to continue working."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteConfirmationModal
       isOpen={showDeleteModal}
  onClose={handleCloseModal}
       
        onConfirm={async () => {
          try {
            if (modalAction === "restore") {
              await handleBulkRestore()
            } else if (modalAction === "delete permanently") {
              await handlePermanentDelete()
            } else {
              await handleConfirmDelete()
            }
          } catch (error) {
            console.error("Operation failed:", error)
          }
        }}
        isLoading={isDeleting || isRestoring}
        message={
          modalAction === "restore"
            ? selectedJobsForDelete.length === 1
              ? `Are you sure you want to restore the selected items "${selectedJobsForDelete[0].job}"? Restoring will reinstate the jobs and make them available for processing again.`
              : `Are you sure you want to restore the selected items ${selectedJobsForDelete.length} jobs?Restoring will reinstate the jobs and make them available for processing again.`
            : selectedJobsForDelete.length === 1
              ? `Are you sure you want to ${
                  modalAction === "delete permanently" ? "permanently delete" : "delete"
                } the job "${selectedJobsForDelete[0].job}"?${
                  modalAction === "delete permanently" ? " This action cannot be undone." : ""
                }`
              : `Are you sure you want to ${
                  modalAction === "delete permanently" ? "permanently delete" : "delete"
                } ${selectedJobsForDelete.length} selected jobs?${
                  modalAction === "delete permanently" ? " This action cannot be undone." : ""
                }`
        }
        confirmText={
          isDeleting || isRestoring
            ? modalAction === "restore"
              ? "Restoring..."
              : "Deleting..."
            : modalAction === "restore"
              ? "Restore"
              : modalAction === "delete permanently"
                ? "Delete Permanently"
                : "Delete"
        }
        confirmClass={modalAction === "restore" ? "btn-primary" : "btn-danger"}
        actionType={modalAction}
      />
    </div>
  )
}

export default SourceProfileTable
